import { NgClass, NgFor, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewEncapsulation,
} from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { DossierCommentsOutputData } from '@sc/data-access-models';
import { ButtonComponent } from '../button';
import { IconComponent } from '../icon';
import { InputTextComponent } from '../input-text';
import { dayjs, DossierComments } from 'functions/commons';

@Component({
  selector: 'sc-dossier-comments-sheet',
  styleUrls: ['./dossier-comments-sheet.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgFor,
    ReactiveFormsModule,
    FormsModule,
    IconComponent,
    ButtonComponent,
    MatSelectModule,
    MatInputModule,
    TranslateModule,
    NgClass,
    InputTextComponent,
  ],
  template: `
    <div class="dossier-comments-container">
      <div class="close">
        <button
          type="button"
          class="close-btn"
          (click)="onButtonClick('cancel')"
        >
          <span class="sr_only">{{
            'GlobalApplicativeConstants.accessibility.closeDialog'
              | translate
                : { dialogTitle: 'DossierCommentsSheet.mainTitle' | translate }
          }}</span>
          <sc-icon name="close"></sc-icon>
        </button>
      </div>
      <h1
        mat-dialog-title
        id="abandon-dossier-title-id"
        [innerHTML]="'DossierCommentsSheet.mainTitle' | translate"
      ></h1>
      <h2 class="mb-l" *ngIf="commentsToDisplay">
        {{ 'DossierCommentsSheet.subTitle' | translate }}
      </h2>

      <div class="commentContainer" *ngIf="commentsToDisplay">
        <ng-container *ngFor="let comment of commentsToDisplay">
          <div class="commentDetails" *ngIf="comment?.display">
            <div class="mb-m commentDetailsHeader">
              <sc-icon
                name="pin"
                [ngClass]="{
                  isPinned: comment?.isPinned,
                }"
                (click)="onPinClick(comment)"
              ></sc-icon>
              <span
                class="sc-design-text-3-bold"
                [innerHTML]="
                  'DossierCommentsSheet.userComment'
                    | translate: { userName: comment?.userName }
                "
              >
              </span>
            </div>
            <span class="dateSpan sc-design-text-4 mb-m">{{
              'DossierCommentsSheet.dateInfos'
                | translate
                  : {
                      weekDay: comment?.dateInfos?.weekDay,
                      date: comment?.dateInfos?.date,
                      hour: comment?.dateInfos?.hour
                    }
            }}</span>
            <span
              class="commentSpan mb-l sc-design-text-3-bold"
              [innerHTML]="comment?.comment"
            ></span>
          </div>
        </ng-container>
      </div>
      <sc-button
        *ngIf="hasAllCommentsDisplayed()"
        buttonType="mainWhite"
        leftIcon="more_horiz"
        class="mb-l moreBtn"
        (click)="onButtonClick('seeAll')"
      >
        {{ 'DossierCommentsSheet.seeAllCommentsBtn' | translate }}</sc-button
      >

      <h2 class="mb-l">
        {{ 'DossierCommentsSheet.addCommentTitle' | translate }}
      </h2>

      <form [formGroup]="form">
        <sc-input-text
          formControlName="userName"
          [label]="'DossierCommentsSheet.nameInput' | translate"
        ></sc-input-text>
        <textarea
          formControlName="comment"
          [placeholder]="'DossierCommentsSheet.commentInput' | translate"
        ></textarea>
      </form>
      <div class="boutonContainer">
        <sc-button buttonType="secondary" (click)="onButtonClick('cancel')">
          {{ 'DossierCommentsSheet.cancelBtn' | translate }}</sc-button
        >
        <sc-button (click)="onButtonClick('save')">
          {{ 'DossierCommentsSheet.saveBtn' | translate }}</sc-button
        >
      </div>
    </div>
  `,
})
export class DossierCommentsSheetComponent implements OnInit {
  @Input() dossierComments: DossierComments[];

  @Output()
  private readonly dossierCommentResult = new EventEmitter<DossierCommentsOutputData>();

  form: FormGroup = this.formBuilder.group({
    userName: [null, Validators.required],
    comment: [null, Validators.required],
  });

  commentsToDisplay: {
    userName: string;
    dateInfos: { weekDay: string; date: string; hour: string };
    comment: string;
    display: boolean;
    isPinned?: boolean;
  }[] = [];

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.commentsToDisplay = [...this.dossierComments]
      ?.sort((comment1, comment2) =>
        new Date(comment1?.timestamp) < new Date(comment2?.timestamp) ? 1 : -1
      )
      ?.map((comment, index) => {
        const timeDayJs = dayjs(comment?.timestamp);
        return {
          ...comment,
          dateInfos: {
            weekDay: timeDayJs.format('dddd'),
            date: timeDayJs.format('DD/MM/YYYY'),
            hour: timeDayJs.format('HH:mm'),
          },
          display: index <= 1 ? true : false,
        };
      });
    this.changeDetectorRef.markForCheck();
  }

  hasAllCommentsDisplayed() {
    return !!this.commentsToDisplay?.filter((comment) => !comment.display)
      ?.length;
  }

  onButtonClick(clickEvent: 'save' | 'cancel' | 'seeAll') {
    if (clickEvent === 'save') {
      const commentsToSave =
        this.dossierComments?.map((comment) => {
          return {
            ...comment,
            isPinned: this.commentsToDisplay?.find(
              (commentDisplayed) =>
                commentDisplayed?.comment === comment?.comment &&
                commentDisplayed?.userName === comment?.userName
            )?.isPinned,
          };
        }) || [];

      if (this.form?.valid) {
        commentsToSave.push({
          ...this.form?.getRawValue(),
          timestamp: new Date(),
          commentType: 'chat',
        });
      }
      this.dossierCommentResult.emit({
        type: clickEvent,
        dossierComments: commentsToSave,
      });
    } else if (clickEvent === 'cancel') {
      this.dossierCommentResult.emit({
        type: clickEvent,
      });
    } else if (clickEvent === 'seeAll') {
      this.commentsToDisplay = this.commentsToDisplay?.map((comment) => {
        return { ...comment, display: true };
      });
    }
  }

  hasPinnedComments() {
    return (
      this.commentsToDisplay?.filter((comment) => comment.isPinned)?.length > 0
    );
  }

  onPinClick(comment) {
    const index = this.commentsToDisplay?.findIndex(
      (commentToFind) => commentToFind === comment
    );

    if (index > -1) {
      comment.isPinned = !comment?.isPinned;
      if (comment.isPinned && this.hasPinnedComments()) {
        this.commentsToDisplay = this.commentsToDisplay.map((comment) => {
          return { ...comment, isPinned: false };
        });
        this.commentsToDisplay = this.commentsToDisplay.map((comment) => {
          return { ...comment, isPinned: false };
        });
      }
      this.commentsToDisplay.splice(index, 1, comment);
    }
  }
}
